import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Amplify } from 'aws-amplify';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SleepProvider, useSleep } from './contexts/SleepContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import AppShell from './components/layout/AppShell';
import ReminderManager from './components/common/ReminderManager';
import { getSetting, setSetting } from './db/sleepDB';
import { isNative } from './utils/platform';
import awsExports from './aws-exports';
import './App.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const SleepLog = lazy(() => import('./pages/SleepLog'));
const Stats = lazy(() => import('./pages/Stats'));
const Settings = lazy(() => import('./pages/Settings'));
const Login = lazy(() => import('./pages/Login'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const Onboarding = lazy(() => import('./pages/Onboarding'));

Amplify.configure(awsExports);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 120000, retry: 1, refetchOnWindowFocus: false },
  },
});

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

// First-run detection: redirect signed-in users into /onboarding when they
// have never completed it AND have zero sleep logs. Anyone with ≥1 log is
// grandfathered — the flag is backfilled so existing users never see it.
// For a fresh device with no local data, the decision waits until the backend
// sync settles ('synced' or 'error') so a returning user's history counts;
// while undecided the normal app renders (Dashboard's empty state).
function OnboardingGate({ children }) {
  const { entryCount, loading, syncStatus } = useSleep();
  const [redirect, setRedirect] = useState(false);
  const decidedRef = useRef(false);

  useEffect(() => {
    if (decidedRef.current || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const done = await getSetting('onboardingCompleted');
        if (cancelled || decidedRef.current) return;
        if (done) {
          decidedRef.current = true;
          return;
        }
        if (entryCount > 0) {
          decidedRef.current = true;
          await setSetting('onboardingCompleted', true);
          return;
        }
        if (syncStatus === 'synced' || syncStatus === 'error') {
          decidedRef.current = true;
          setRedirect(true);
        }
      } catch (err) {
        console.warn('Onboarding check failed (non-fatal):', err);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, entryCount, syncStatus]);

  if (redirect) return <Navigate to="/onboarding" replace />;
  return children;
}

function IAPInitializer() {
  useEffect(() => {
    if (isNative()) {
      import('./services/iap').then(({ initializeIAP }) => {
        initializeIAP();
      });
    }
  }, []);
  return null;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <SleepProvider>
                  {/* iOS morning reminder: one-time tap-listener + schedule re-assert */}
                  <ReminderManager />
                  <Routes>
                    {/* Authenticated but OUTSIDE AppShell — onboarding has no nav chrome */}
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route
                      path="/*"
                      element={
                        <OnboardingGate>
                          <AppShell>
                            <Routes>
                              <Route path="/" element={<Dashboard />} />
                              <Route path="/log" element={<SleepLog />} />
                              <Route path="/stats" element={<Stats />} />
                              <Route path="/settings" element={<Settings />} />
                            </Routes>
                          </AppShell>
                        </OnboardingGate>
                      }
                    />
                  </Routes>
                </SleepProvider>
              </SubscriptionProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            {/* Pre-warm CdvPurchase as soon as the app boots — not gated on auth.
                Reviewer flow taps Subscribe shortly after sign-in, so the store
                needs time to fetch StoreKit metadata before the paywall opens. */}
            <IAPInitializer />
            <AppRoutes />
            <Toaster
              position="top-center"
              containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
              toastOptions={{
                duration: 3500,
                style: {
                  borderRadius: '14px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  maxWidth: '92vw',
                  background: 'var(--toast-bg)',
                  color: 'var(--toast-fg)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
                  border: '1px solid var(--toast-border)',
                },
                success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
                error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
              }}
            />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
